import * as turf from '@turf/turf'

/**
 * @see {@link https://gist.github.com/springmeyer/871897}
 */
function degreesToMeters( px ) {
  const x = px[0] * 20037508.34 / 180
  let y = Math.log( Math.tan( ( 90 + px[1] ) * Math.PI / 360 ) ) / ( Math.PI / 180 )
  y = y * 20037508.34 / 180
  return [ x, y ]
}

/**
 * @see {@link https://gist.github.com/springmeyer/871897}
 */
function metersToDegrees( px ) {
  // gist from https://gist.github.com/springmeyer/871897
  const lon = px[0] * 180 / 20037508.34
  const lat = Math.atan( Math.exp( px[1] * Math.PI / 20037508.34 ) ) * 360 / Math.PI - 90
  return [ lon, lat ]
}

/**
 * Prevent excessive re-rendering by wrapping function with this 
 * @param {function} fn function to wrap
 * @param {Number} wait time in ms to wait before resolving func
 */
function debounce( fn, wait ) {
  let t
  return function() {
    clearTimeout( t )
    t = setTimeout( () => fn.apply( this, arguments ), wait )
  }
}

/**
 * Creates a unique hash based on coordinates. Used to kick the image 
 * layer into re-rendering if the geoshape has moved
 * @param {Array<Number[]>} coordinates [ [lng, lat], [lng, lat ] ]
 */
function makeHash( coordinates ) {
  let str = ''  
  coordinates.forEach( ( pair ) => {
    str += `${pair[0]}${pair[1]}`
  } )
  let hash = 0
  let chr
  for ( let i = 0; i < str.length; i++ ) {
    chr = str.charCodeAt( i )
    hash = ( ( hash << 5 ) - hash ) + chr
    hash |= 0
  }
  return `${hash}`
}

/**
 * @param {Number[]} coords
 */
function calcMidpoint( coords ) {
  const points = coords.map( coord => turf.point( coord ) )
  const { geometry : { coordinates } } = turf.midpoint( ...points )
  return coordinates
}

/**
 * @param {Array<Number[]>} coordinates
 * @returns {Array<Number[]>} the bounds of the tile draw area
 */
function getBounds( coordinates ) {
  if ( !coordinates ) return []
  const lng = 0
  const lat = 1
  const xMin = Math.min( ...coordinates.map( c => c[lat] ) ) 
  const xMax = Math.max( ...coordinates.map( c => c[lat] ) ) 
  const yMin = Math.min( ...coordinates.map( c => c[lng] ) ) 
  const yMax = Math.max( ...coordinates.map( c => c[lng] ) ) 
  return [ [ xMax, yMin ], [ xMax, yMax ], [ xMin, yMax ], [ xMin, yMin ] ]
  
}
/**
 * @param {Array<Number[]>} coordinates
 * @returns {Array<Number[]>} the bounds of the tile draw area
 */
function getLayerBounds( coordinates ) {
  if ( !coordinates ) return []
  const lng = 0
  const lat = 1
  const xMin = Math.min( ...coordinates.map( c => c[lat] ) ) 
  const xMax = Math.max( ...coordinates.map( c => c[lat] ) ) 
  const yMin = Math.min( ...coordinates.map( c => c[lng] ) ) 
  const yMax = Math.max( ...coordinates.map( c => c[lng] ) ) 
  // [sw.lng, sw.lat, ne.lng, ne.lat]
  return [ yMin, xMin, yMax, xMax ]
}

/**
 * Get the vertex coordinates and center coords of a feature
 * @param {Object} feature geojson feature
 * @returns {Object} {center : Array, coordinates : Array} 
 */
function getCoordinatesAndCenter( feature ) {
  const { geometry : { coordinates } } = feature
  const centerPt = turf.center( feature )
  const center = centerPt.geometry.coordinates
  return { center, coordinates : coordinates[0] }
}

/**
 * Assembles all of the data needed to generate a new tile
 * @param {Number} zoom the zoom level
 * @param {Object} feature the geojson feature defining the boundaries
 * @param {String} sourceImagePath url for image
 * @param {Object} metadata the existing metadata for the tile
 * @returns {FormData}
 */
async function createTileFormData( { zoom, feature, metadata } ) {
  const { coordinates, center } = getCoordinatesAndCenter( feature )
  const toSend = new FormData()
  const minzoom = zoom.minZoom
  const maxzoom = zoom.maxZoom
  toSend.append( 'metadata', JSON.stringify( { 
    ...metadata, 
    bounds : getBounds( coordinates ),
    center,
    feature,
    maxzoom,
    minzoom
  } ) )
  toSend.append( 'zoom', `${minzoom}-${maxzoom}` )
  toSend.append( 'coordinates', JSON.stringify( coordinates ) )
  return toSend
}

/**
 * Formats data for adding a tile layer to react-mapbox-gl
 * @param { Object } geotile 
 * @returns {Object} 
 */
function getLayerData( geotile ) {
  if ( !geotile.isGenerated ||
       geotile.isGenerating ||
       !geotile?.tilePath ||
       !geotile?.coordinates ) {
    return null
  }
  const { zoom, tilePath, coordinates } = geotile
  const zoomSplit = zoom.split( '-' )
  const minzoom = zoomSplit[0]
  const maxzoom = zoomSplit[1] || zoomSplit[0]
  return { 
    type : 'raster', 
    minzoom : parseInt( minzoom ), 
    maxzoom : parseInt( maxzoom ), 
    bounds : getLayerBounds( coordinates ), 
    scheme : 'xyz', 
    tiles : [ `${tilePath}/{z}/{x}/{y}.png` ], 
    tileSize : 256 
  }
}

module.exports = {
  degreesToMeters,
  metersToDegrees, 
  debounce,
  makeHash,
  calcMidpoint,
  getLayerData,
  getBounds,
  createTileFormData
}
